import React, { PropsWithChildren, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useProductSwitchModal } from '@/hooks/use-product-switch';
import { designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

import { PRODUCT_CHIPS_HEIGHT, ProductSwitcherChips } from './ProductSwitcherChips';

/**
 * A wrapper component for the lobby content that includes the product switcher chips and handle.
 *
 * In `LobbyProductSwitcherChipsHandle.tsx`, the chips are animated relative to the scroll offset
 * to appear "static" at the top of the screen.
 * However, since `overflow: hidden` is used to prevent the handle's shadow from leaking outside,
 * the chips become hidden when overscrolling.
 *
 * To address this, this wrapper renders an additional set of chips just below the ones in the
 * handle component. This ensures that even when overscrolled, a visible set of chips remains
 * at the top, maintaining a consistent look and feel across all products.
 */
export const LobbyWrapper = ({ children }: PropsWithChildren) => {
    const insets = useSafeAreaInsets();

    const { switchingAvailable } = useProductSwitchModal();

    if (!switchingAvailable) {
        return (
            <Box flex={1} backgroundColor={ds.colorToken.background.neutral.base}>
                <Box
                    position={'absolute'}
                    backgroundColor={ds.colorToken.background.neutral.base}
                    width={'100%'}
                    height={insets.top}
                    top={0}
                    zIndex={1}
                />
                {children}
            </Box>
        );
    }

    return (
        <Box flex={1} backgroundColor={ds.colorToken.background.brand.strong}>
            {/**don't render on web as the chips would show 2 times when overscrolling */}
            {!isWeb ? (
                <Box style={{ paddingTop: insets.top }} position={'absolute'} width={'100%'}>
                    <ProductSwitcherChips />
                </Box>
            ) : null}
            <Box position={'absolute'} top={0} bottom={0} left={0} right={0} pointerEvents="box-none">
                {children}
            </Box>
            <Box
                zIndex={-1}
                position={'absolute'}
                top="50%"
                bottom={0}
                left={0}
                right={0}
                backgroundColor={ds.colorToken.background.neutral.base}
            />
        </Box>
    );
};

export const LOBBY_HANDLE_HEIGHT = 28;
export const LOBBY_HANDLE_BORDER_RADIUS = 32;

export const useLobbyListStyles = () => {
    const insets = useSafeAreaInsets();
    const { switchingAvailable } = useProductSwitchModal();

    const refreshControlOffset = useMemo(() => {
        // 8 represents half of the padding bottom of the product chips
        // this makes the refresh control appear at the right position (centered under the chips)
        return switchingAvailable ? PRODUCT_CHIPS_HEIGHT + insets.top - 8 : insets.top;
    }, [insets.top, switchingAvailable]);

    // the height of the handle is only applied if product switching is available
    // this is used to calculate the sticky position of the items in the lobby list
    const lobbyHandleHeight = switchingAvailable ? LOBBY_HANDLE_HEIGHT : 0;

    return {
        refreshControlOffset,
        lobbyHandleHeight,
    };
};

export const productSwitcherStyles = StyleSheet.create({
    lobbySheetShadow: {
        shadowColor: designSystem.colors.neutralBlack,
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.25,
        shadowRadius: 40,
        elevation: 12,
        backgroundColor: ds.colorValue.background.neutral.base,
        borderTopLeftRadius: LOBBY_HANDLE_BORDER_RADIUS,
        borderTopRightRadius: LOBBY_HANDLE_BORDER_RADIUS,
        borderCurve: 'continuous',
    },
    lobbyHandle: {
        paddingBottom: 12,
        backgroundColor: ds.colorValue.background.neutral.base,
        width: '100%',
        borderTopLeftRadius: 32,
        borderTopRightRadius: 32,
    },
    borderRadius: {
        borderTopLeftRadius: LOBBY_HANDLE_BORDER_RADIUS,
        borderTopRightRadius: LOBBY_HANDLE_BORDER_RADIUS,
        borderCurve: 'continuous',
        overflow: 'hidden',
    },
    refreshControlStyle: {
        // force the refresh control to be on top of the product chips
        zIndex: 9999,
    },
    lobbyListContentContainerStyle: {
        backgroundColor: ds.colorValue.background.neutral.base,
    },
});
